import { Observable, Subject } from 'rxjs';

export interface UserInterests {
  id: number;
  active: boolean;
  formQuestionDTOList: QuestionDTO[];
  codCliente: string;
  name: string;
  mandatory: boolean;
}

export interface Response {
  id: number;
  text: string;
  uuid: string;
  formResponseDTOList?: Response[];
}

export interface UserResponse {
  questionId: number;
  response: string[]; // uuid
}

export type QuestionType = 'RADIOBUTTON' | 'TAGS';

export interface QuestionDTO {
  id: number;
  title: string;
  description: string;
  multiple?: boolean;
  type: QuestionType;
  formResponseDTOList: Response[];
  responses?: string[];
}

export class Question {
  private _id: number;
  private _title: string;
  private _description: string;
  private _multiple?: boolean;
  private _type: QuestionType;
  private _userResponsesPerLevel: string[][];
  private _availableResponsesPerLevel: Response[][];

  private _selectedResponseCurrentLevel: Response;
  private _$changedEvent: Subject<void> = new Subject();

  private _currentLevelIndex: number;

  private constructor(
    id: number,
    title: string,
    description: string,
    type: QuestionType,
    formResponseDTOList: Response[],
    multiple?: boolean
  ) {
    this._id = id;
    this._title = title;
    this._description = description;
    this._multiple = multiple;
    this._type = type;
    this._multiple = multiple != null ? multiple : false;

    // Tracking variables
    this._currentLevelIndex = 0;
    this._userResponsesPerLevel = []; // min size = 0;
    this._availableResponsesPerLevel = [formResponseDTOList]; // min size = 1 -> the first level
  }

  public static fromDTO(dto: QuestionDTO) {
    return new Question(dto.id, dto.title, dto.description, dto.type, dto.formResponseDTOList, dto.multiple);
  }

  public static fromDTOWithUserResponses(dto: QuestionDTO, userResponses: UserResponse[]) {
    const instance = this.fromDTO(dto);
    const userResponse = userResponses.find((ur) => ur.questionId === dto.id);
    if (userResponse != null && userResponse.response.length > 0) {
      if (instance.type === 'RADIOBUTTON') {
        const availableResponsesPerLevel = [];
        const userResponsesPerLevel = [];
        instance.exloreAndCaptureUserResponses(
          userResponse.response[0],
          dto.formResponseDTOList,
          availableResponsesPerLevel,
          userResponsesPerLevel
        );
        if (availableResponsesPerLevel.length === 0) {
          availableResponsesPerLevel.push(dto.formResponseDTOList);
        } else {
          availableResponsesPerLevel.reverse();
        }
        userResponsesPerLevel.reverse();
        instance._availableResponsesPerLevel = availableResponsesPerLevel;
        instance._userResponsesPerLevel = userResponsesPerLevel;
      } else {
        userResponse.response.forEach((ur) => {
          instance.provideUserResponseForCurrentLevel(ur);
        });
      }
    }
    return instance;
  }

  private exloreAndCaptureUserResponses(
    responseUUID: string,
    availableResponsesCurrentLevel: Response[],
    availableResponsesPerLevel: Response[][],
    userResponsesPerLevel: string[][]
  ) {
    if (availableResponsesCurrentLevel == null || availableResponsesCurrentLevel.length == 0) {
      return false;
    }
    const found = availableResponsesCurrentLevel.find((ar) => ar.uuid === responseUUID);
    if (found) {
      userResponsesPerLevel.push([responseUUID]);
      availableResponsesPerLevel.push(availableResponsesCurrentLevel);
      return true;
    } else {
      for (const ar of availableResponsesCurrentLevel) {
        if (
          this.exloreAndCaptureUserResponses(
            responseUUID,
            ar.formResponseDTOList,
            availableResponsesPerLevel,
            userResponsesPerLevel
          )
        ) {
          availableResponsesPerLevel.push(availableResponsesCurrentLevel);
          userResponsesPerLevel.push([ar.uuid]);
          return true;
        }
      }
      return false;
    }
  }

  public provideUserResponseForCurrentLevel(responseUUID: string) {
    const currentResponses = this.getUserResponsesForCurrentLevel();
    if (currentResponses != null) {
      if (!this._multiple) {
        if (currentResponses[0] !== responseUUID) {
          this._userResponsesPerLevel.splice(this._currentLevelIndex);
          this._userResponsesPerLevel.push([responseUUID]);
          if (this._availableResponsesPerLevel.length > this._currentLevelIndex + 1) {
            this._availableResponsesPerLevel.splice(this._currentLevelIndex + 1);
          }
        }
      } else {
        if (!currentResponses.find((r) => r === responseUUID)) {
          currentResponses.push(responseUUID);
        }
      }
    } else {
      this._userResponsesPerLevel.push([responseUUID]);
    }
    this._selectedResponseCurrentLevel = this.availableResponsesForCurrentLevel.find((r) => r.uuid === responseUUID);
  }

  public provideResponseForLevel(level: number, responseUUID: string) {
    this._currentLevelIndex = level;
    this.provideUserResponseForCurrentLevel(responseUUID);
    if (this.hasNextLevel()) {
      this.goToNextLevel();
    } else {
      this._$changedEvent.next();
    }
  }

  public removeUserResponseFromCurrentLevel(responseUUID: string) {
    const current = this.getUserResponsesForCurrentLevel();
    if (this.type === 'TAGS' && current != null) {
      this._userResponsesPerLevel[this._currentLevelIndex] = current.filter((r) => r !== responseUUID);
      if (this._userResponsesPerLevel[this._currentLevelIndex].length === 0) {
        this._userResponsesPerLevel.splice(this._currentLevelIndex);
      }
    }
  }

  public hasNextLevel(): boolean {
    if (this._multiple) {
      return false;
    }

    const userSelectedResponses = this.getUserResponsesForCurrentLevel();
    if (!userSelectedResponses) {
      return false;
    }
    const selectedResponseAsRaw = this._availableResponsesPerLevel[this._currentLevelIndex].find(
      (r) => r.uuid === userSelectedResponses[0]
    );
    return selectedResponseAsRaw.formResponseDTOList != null && selectedResponseAsRaw.formResponseDTOList.length > 0;
  }

  public goToNextLevel() {
    const userResponsesCurrentLevel = this.getUserResponsesForCurrentLevel();
    if (userResponsesCurrentLevel != null && this.hasNextLevel()) {
      if (this._availableResponsesPerLevel.length === this._currentLevelIndex + 1) {
        this._availableResponsesPerLevel.push(this._selectedResponseCurrentLevel.formResponseDTOList);
      }
      this._currentLevelIndex++;
      this._$changedEvent.next();
    }
  }

  public hasPreviousLevel() {
    return this._currentLevelIndex > 0;
  }

  public goToPreviousLevel() {
    if (this._currentLevelIndex > 0) {
      this._currentLevelIndex--;
      this._$changedEvent.next();
    }
  }

  public get availableResponsesForCurrentLevel() {
    return this._availableResponsesPerLevel[this._currentLevelIndex];
  }

  public get userResponsesForCurrentLevel(): string[] {
    const userResponsesForCurrentLevel = this.getUserResponsesForCurrentLevel();
    return userResponsesForCurrentLevel ? userResponsesForCurrentLevel : [];
  }

  public get changedEvent(): Observable<void> {
    return this._$changedEvent.asObservable();
  }

  private getUserResponsesForCurrentLevel(): string[] {
    return this._userResponsesPerLevel.length > 0 ? this._userResponsesPerLevel[this._currentLevelIndex] : null;
  }

  public getAvailableResponsesForLevel(level: number): Response[] {
    if (this._availableResponsesPerLevel.length <= level) {
      return null;
    }
    return this._availableResponsesPerLevel[level];
  }

  public getUserResponsesForLevel(level: number): string[] {
    if (this._userResponsesPerLevel.length <= level) {
      return null;
    }
    return this._userResponsesPerLevel[level];
  }

  public get id() {
    return this._id;
  }

  public get currentlevel() {
    return this._currentLevelIndex;
  }

  public get title() {
    return this._title;
  }

  public get description() {
    return this._description;
  }

  public get type() {
    return this._type;
  }

  public get multiple() {
    return this._multiple;
  }

  public get lastSelectedResponse() {
    return this._selectedResponseCurrentLevel;
  }

  public get allUserResponses() {
    return this._userResponsesPerLevel.flatMap((r) => r);
  }

  public get completed() {
    if (this.type === 'RADIOBUTTON') {
      const i = this._userResponsesPerLevel.length - 1;
      if (i < 0) {
        return false;
      }
      const lastUserResponse = this._userResponsesPerLevel[i][0];
      const childAvailableResponses = this._availableResponsesPerLevel[i].find(
        (ar) => ar.uuid === lastUserResponse
      ).formResponseDTOList;
      return childAvailableResponses == null || childAvailableResponses.length === 0;
    }
    return true;
  }

  public get finalUserResponses() {
    const finalUserResponses = this._userResponsesPerLevel[this._userResponsesPerLevel.length - 1];
    if (this.completed) {
      if (finalUserResponses == null || finalUserResponses.length === 0) {
        return [];
      }
      return finalUserResponses;
    }
    return null;
  }
}
